import { Download, ExternalLink, Printer } from 'lucide-react';
import { type ReactNode, useRef } from 'react';
import { Button } from '#client/components/ui/Button';
import { Spinner } from '#client/components/ui/Spinner';
import { LIGHT_SCOPE } from '#client/providers/ThemeProvider';
import { cn } from '#client/lib/cn';

interface PdfFrameProps {
  /** The PDF to show. `undefined` renders the pending state. */
  src: string | undefined;
  title: string;
  /** Where Download points. Omit to hide the button — the builder has nothing to save. */
  downloadUrl?: string | undefined;
  /** Rendered in the toolbar before the actions. */
  toolbar?: ReactNode | undefined;
  /** Shown instead of the document, e.g. a render failure. */
  message?: string | undefined;
  className?: string | undefined;
}

/**
 * A PDF, with the three things anyone wants to do with one.
 *
 * The document is loaded straight from its URL rather than rendered into a blob
 * on the client, which is what lets Download be an ordinary link and Open be an
 * ordinary tab. The previous screen built the PDF in the browser and could offer
 * neither: there was a blob URL that existed only inside that React tree, and it
 * leaked on every visit.
 */
export function PdfFrame({ src, title, downloadUrl, toolbar, message, className }: PdfFrameProps) {
  const frame = useRef<HTMLIFrameElement>(null);

  /**
   * Prints the embedded document.
   *
   * `contentWindow.print()` drives the viewer that is already showing the right
   * document, so the operator sees the print dialog without a tab appearing.
   * Browsers differ on whether a plugin-backed PDF frame allows it, so a failure
   * falls back to opening the file — which every browser can print. Printing the
   * *page* instead would print the app's chrome around a viewer, which is the
   * failure mode worth ruling out.
   */
  function handlePrint() {
    try {
      const view = frame.current?.contentWindow;
      if (!view) throw new Error('The document is not ready');
      view.focus();
      view.print();
    } catch {
      if (src) window.open(src, '_blank', 'noopener');
    }
  }

  return (
    <div className={cn('c-pdf', className)}>
      <div className="c-pdf__toolbar">
        <div className="c-pdf__toolbar-slot">{toolbar}</div>

        <Button icon={<Printer />} onClick={handlePrint} disabled={!src} variant="primary">
          Print
        </Button>
        {downloadUrl && (
          // A real anchor, not a button that assigns to `location`. The browser
          // then does its own download handling and takes the file name from the
          // server's Content-Disposition — and, unlike a navigation, it cannot
          // take the operator off the screen if that header is ever wrong.
          // Styled as a button because that is what it is to the person using it.
          <a className="c-button c-button--secondary c-button--md" href={downloadUrl} download>
            <span className="c-button__icon">
              <Download />
            </span>
            <span>Download</span>
          </a>
        )}
        <Button
          icon={<ExternalLink />}
          aria-label="Open in a new tab"
          onClick={() => src && window.open(src, '_blank', 'noopener')}
          disabled={!src}
        />
      </div>

      {/* See LIGHT_SCOPE — a rendered PDF is always a white page, so the frame
          around it stays light even when the app is dark. */}
      <div className={cn('c-pdf__stage', LIGHT_SCOPE)}>
        {message ? (
          <p className="c-pdf__message">{message}</p>
        ) : src ? (
          <iframe ref={frame} className="c-pdf__frame" src={src} title={title} />
        ) : (
          <div className="c-pdf__pending">
            <Spinner size="lg" label="Rendering" showLabel />
          </div>
        )}
      </div>
    </div>
  );
}
